export interface SuperAdminPendingApprovalJobs {
  job_id: string;
  job_title?: string;
  request_id: string;
  current_level?: number;
  total_levels?: number;
  submitted_at?: string;
  company_id?: string;
  company_name?: string;
  job_description?: string;
  department?: string;
  location?: string;
  job_type?: string;
  work_mode?: string;
  posting_visibility?: string;
  status: string;
  [key: string]: unknown;
}

export type ApprovalTableRow = Record<string, unknown>;
export type ApprovalStatus = "pending" | "approved" | "rejected" | "other";
export type JobsTabKey = "pending" | "history";
export type HistoryStatusFilter = "all" | "pending" | "approved" | "rejected";

export interface ApprovalStatsSummary {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

export interface ApprovalSummaryCard {
  label: string;
  value: number;
  accent: string;
  filter: HistoryStatusFilter;
}

export interface ApprovalTab {
  key: JobsTabKey;
  label: string;
  count: number;
}

export interface ApprovalHistoryQuery {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface PendingApprovalsQuery {
  limit?: number;
  offset?: number;
}

export interface PendingApprovalsLoadResult {
  approvals: SuperAdminPendingApprovalJobs[];
  total: number;
  hasMore: boolean;
  error?: string;
}

export interface ApprovalHistoryLoadResult {
  history: ApprovalTableRow[];
  total: number;
  hasMore: boolean;
  error?: string;
}

export interface ApprovalHistoryTableRow {
  key: string;
  companyName: string;
  jobTitle: string;
  showJobTitle: boolean;
  statusLabel: string;
  statusPillClasses: string;
  submittedAt: string;
  reviewedAt: string;
  reviewedByName: string;
  rejectionReason: string;
  source: ApprovalTableRow;
}

export const emptyApprovalStats: ApprovalStatsSummary = {
  pending: 0,
  approved: 0,
  rejected: 0,
  total: 0,
};

export function normalizeApprovalStats(
  stats?: Partial<ApprovalStatsSummary> | null
): ApprovalStatsSummary {
  return {
    pending: toCount(stats?.pending),
    approved: toCount(stats?.approved),
    rejected: toCount(stats?.rejected),
    total: toCount(stats?.total),
  };
}

export function toApprovalHistoryTableRow(
  row: ApprovalTableRow,
  index: number
): ApprovalHistoryTableRow {
  const status = normalizeApprovalStatus(row.status);
  const companyValue = getStringValue(row, ["company_name", "company"]);
  const jobTitle = getStringValue(row, ["job_title", "title"]);

  return {
    key: getApprovalKey(row, index),
    companyName: companyValue || jobTitle || "-",
    jobTitle,
    showJobTitle: Boolean(companyValue && jobTitle),
    statusLabel: formatStatusLabel(status),
    statusPillClasses: getStatusPillClasses(status),
    submittedAt: formatApprovalDate(row.submitted_at),
    reviewedAt: formatApprovalDate(row.reviewed_at),
    reviewedByName:
      getStringValue(row, [
        "reviewed_by_name",
        "reviewed_by",
        "reviewer_name",
      ]) || "-",
    rejectionReason:
      getStringValue(row, [
        "rejection_reason",
        "reject_reason",
        "rejectionReason",
        "comments",
      ]) || "-",
    source: row,
  };
}

export function normalizeApprovalStatus(status: unknown): ApprovalStatus {
  const normalized = String(status || "pending")
    .trim()
    .toLowerCase();

  if (normalized.includes("approve")) return "approved";
  if (normalized.includes("reject")) return "rejected";
  if (normalized.includes("pending")) return "pending";
  return "other";
}

export function getStatusPillClasses(status: ApprovalStatus) {
  if (status === "approved") return "bg-green-100 text-green-800";
  if (status === "rejected") return "bg-red-100 text-red-800";
  if (status === "pending") return "bg-yellow-100 text-yellow-800";
  return "bg-gray-100 text-gray-700";
}

export function formatStatusLabel(status: ApprovalStatus) {
  return status === "other" ? "UNKNOWN" : status.toUpperCase();
}

export function getStringValue(row: ApprovalTableRow, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return String(value);
    }
  }

  return "";
}

export function getApprovalDetailState(row: ApprovalTableRow) {
  const detailState: ApprovalTableRow = {};

  for (const key in row) {
    if (!Object.prototype.hasOwnProperty.call(row, key)) continue;
    if (!isIdentifierField(key)) {
      detailState[key] = row[key];
    }
  }

  return detailState;
}

export function formatApprovalDate(value: unknown) {
  if (!value) return "-";

  const rawValue = String(value);
  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return rawValue || "-";

  const pad = (part: number) => (part < 10 ? `0${part}` : String(part));
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toCount(value: unknown) {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
}

function getApprovalKey(row: ApprovalTableRow, index: number) {
  return (
    getStringValue(row, ["request_id", "job_id", "id"]) || `approval-${index}`
  );
}

function isIdentifierField(key: string) {
  const normalized = key.trim().toLowerCase();
  return (
    normalized === "id" ||
    normalized === "uuid" ||
    normalized.endsWith("_id") ||
    normalized.endsWith("_uuid")
  );
}
